import ApiHelpers from "@/data/dao/base/ApiHelpers";
import ImportHistory from "@/data/models/importHistory";
import Meter, { AlertingMeter, FlowMeter } from "@/data/models/meter";
import { ImportType } from "@/data/models/enums/import-type-enum";
import config from "@/config";

export interface AdminOverview {
  alertingMeters: { count: number, items: AlertingMeter[] };
  failedImports: { count24h: number, lastFailedAt: Date | null, filenames: string[] };
  clientsCount: number;
  firmwares: { count: number, latest: { version: string, createdAt: string } | null };
}

export default class LogsDao {
  static getAll(showSuccess: boolean): Promise<Array<ImportHistory>> {
    // If showSuccess is true add ?all to the url to show all logs, otherwise only show failed logs
    // Sans le contenu brut (volumineux), chargé à la demande avec getRaw
    const url = showSuccess ? `/admin/logs?all=true` : `/admin/logs`;
    return ApiHelpers.get<Array<ImportHistory>>(url).then((data) => data.map(it => ImportHistory.map(it)));
  }

  static replay(id: string): Promise<void> {
    return ApiHelpers.post(`/admin/logs/replay/${id}`, {});
  }

  static getAlertMeters(): Promise<AlertingMeter[]> {
    return ApiHelpers.get<Meter[]>(`/admin/logs/alert/meters`).then(data => data.map(it => AlertingMeter.map(it)));
  }

  // Indicateurs de l'accueil de l'administration (nombres et 3 premiers éléments de chaque carte)
  static getOverview(): Promise<AdminOverview> {
    return ApiHelpers.get<AdminOverview>(`/admin/overview`).then((data) => ({
      ...data,
      alertingMeters: {count: data.alertingMeters.count, items: data.alertingMeters.items.map((it) => AlertingMeter.map(it))},
      failedImports: {...data.failedImports, lastFailedAt: data.failedImports.lastFailedAt ? new Date(data.failedImports.lastFailedAt) : null},
    }));
  }

  // Dernière remontée de chaque compteur recevant des données
  static getDataFlow(): Promise<FlowMeter[]> {
    return ApiHelpers.get<FlowMeter[]>(`/admin/logs/flow`).then(data => data.map(it => FlowMeter.map(it)));
  }

  // Flux temps réel (SSE) des relevés insérés : EventSource ne peut pas envoyer d'en-tête, le jeton passe dans l'URL
  static openDataFlowStream(onReadings: (readings: { meterId: string, date: Date }[]) => void): Promise<EventSource> {
    return ApiHelpers.getBearer().then((token) => {
      const source = new EventSource(`${config.api.endpoint}/admin/logs/flow/stream?access_token=${encodeURIComponent(token)}`);
      source.addEventListener('readings', (event: Event) => {
        onReadings((JSON.parse((event as MessageEvent).data) as { meterId: string, date: string }[]).map((it) => ({meterId: it.meterId, date: new Date(it.date)})));
      });
      return source;
    });
  }

  // Contenu brut d'un import (les listes ne le renvoient pas)
  static getRaw(id: string): Promise<string> {
    return ApiHelpers.get<{ raw: string }>(`/admin/logs/raw/${id}`).then((data) => data.raw || '');
  }

  // admin/logs/filename/:filename
  static getByFilename(filename: string): Promise<Array<ImportHistory>> {
    return ApiHelpers.get<Array<ImportHistory>>(`/admin/logs/filename/${filename}`).then((data) => data.map(it => ImportHistory.map(it)));
  }

  // admin/logs/alert/scilab/:meterId
  static getScilabLogs(meterId: string): Promise<Array<string>> {
    return ApiHelpers.get<Array<string>>(`/admin/logs/alert/scilab/${meterId}`);
  }

  // POST /import/start/:type?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
  // startDate & endDate are optionnal
  static startImport(importType: ImportType, startDate?: string, endDate?: string): Promise<boolean> {
    const args = [];
    if (startDate) args.push(`startDate=${startDate}`);
    if (endDate) args.push(`endDate=${endDate}`);
    return ApiHelpers.post<boolean>(`/admin/meter/import/start/${importType}?${args.join("&")}`, {});
  }
}
